import type { TFunction } from 'i18next'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import { useSelectVariantsMutation, useUndoVariantSelectMutation } from '#api/tmsApiHooks'
import type { AppDispatch } from '#app/store'
import { productFamilyContainsString, productVariantContainsString } from '#app/store/hooks/useFilteredProductGroups'
import {
  addEquipmentVariant,
  addNewOptionalEquipmentPackage,
  type ConfigurationOptionalEquipment,
  type ConfigurationOptionalEquipmentId,
  createOptionalEquipmentId,
  duplicateEquipment as duplicateEquipmentAction,
  removeEquipment as removeEquipmentAction,
  removeEquipmentVariant,
  replaceEquipmentImplicitVariants,
  setEquipmentStatus as setEquipmentStatusAction,
  type TruckConfiguration,
  updateOptionalEquipmentTitle,
} from '#app/store/slices/configurationSlice'
import { type ConfigurationEnrichmentData, enrichConfiguration } from '#app/store/slices/enrichConfiguration'
import type { AppProductVariant, ProductPath, VariantProductPath } from '#app/types/AppTypes'
import type { FavoriteSetId } from '#features/FavoriteSets/FavoriteSet'
import { hasPrintOptionsChanged } from '#features/PrintOptions/utils'
import { useToast } from '#features/Toasts/useToast'
import { type ComparisonResult, useChangesTable } from '../Changes/useChangesTable'
import { useConfigurationDifference } from '../Comparison/useConfigurationDifference'
import { getAllVariants } from '../utils/collections'
import { OverwrittenToastMessage } from './OverwrittenToastWarning'
import { equipmentName } from './utils'

export type OptionalEquipmentContext = ReturnType<typeof useOptionalEquipment>
export type EnhancedOptionalEquipment = Exclude<ReturnType<typeof mapToEnhancedEquipment>, undefined>

const mapToEnhancedVariant = (
  enrichment: ConfigurationEnrichmentData,
  variant: ConfigurationOptionalEquipment['variants'][number] | ConfigurationOptionalEquipment['implicitChanges'][number],
) => {
  const source = variant.sourceVariant ? enrichment.enrichedVariants[`${variant.family.id}${variant.sourceVariant.id}`] : undefined
  const target = enrichment.enrichedVariants[`${variant.family.id}${variant.targetVariant.id}`]
  const family = target?.parentFamily ?? source?.parentFamily ?? enrichment.enrichedFamilies[variant.family.id]

  return {
    family,
    source,
    target,
    sourceFamily: variant.family,
    sourceVariant: variant.sourceVariant,
    targetVariant: variant.targetVariant,
    properties: {
      isValidTarget: !family ? 'family-invalid' : !target ? 'target-invalid' : 'valid',
      isApplicable: family?.selectedVariant?.id !== target?.id,
      hasDetails: !!target?.cosima,
      hasPrintOptionsChanged: hasPrintOptionsChanged(target?.printOptions),
    },
  }
}
const mapToEnhancedEquipment = (enrichment: ConfigurationEnrichmentData, equipment: ConfigurationOptionalEquipment, t: TFunction) => {
  if (equipment.status === 'removed') {
    return undefined
  }
  const variants = equipment.variants.map((variant) => mapToEnhancedVariant(enrichment, variant))
  const selectedPriceDelta = variants.map((x) => (x.targetVariant.price ?? 0) - (x.sourceVariant?.price ?? 0)).reduce((a, b) => a + b, 0)
  const applicableVariants = variants.filter((x) => x.properties.isApplicable).length

  const implicitChanges = equipment.implicitChanges.map((variant) => mapToEnhancedVariant(enrichment, variant))
  const implicitPriceDelta = implicitChanges.map((x) => (x.targetVariant.price ?? 0) - (x.sourceVariant?.price ?? 0)).reduce((a, b) => a + b, 0)
  const applicableImplicitVariants = variants.filter((x) => x.properties.isApplicable).length

  return {
    ...equipment,
    title: equipmentName(equipment, t),
    variants,
    priceDelta: selectedPriceDelta + implicitPriceDelta,
    implicitChanges,
    applicableChanges: applicableVariants + applicableImplicitVariants,
    // placeholder
    suggestions: [],
  }
}

export const useEquipment = (configuration: TruckConfiguration | undefined, enrichment: ConfigurationEnrichmentData | undefined) => {
  const { t } = useTranslation()
  const { packages, byId } = useMemo(() => {
    if (!enrichment) {
      return { packages: [], byId: {} }
    }
    const equipments = (configuration?.optionalEquipment ?? [])
      .map((equipment) => mapToEnhancedEquipment(enrichment, equipment, t))
      .filter((x) => x !== undefined)

    const byId: Record<ConfigurationOptionalEquipmentId, (typeof equipments)[number]> = {}
    for (const equipment of equipments) {
      byId[equipment.id] = equipment
    }
    return {
      packages: equipments,
      byId,
    }
  }, [configuration, enrichment, t])
  return {
    equipment: packages,
    byId,
  }
}

export const useEquipmentActions = (
  dispatch: AppDispatch,
  configuration: TruckConfiguration | undefined,
  enrichment: ConfigurationEnrichmentData | undefined,
) => {
  const toaster = useToast()
  const createNewEquipment = useCallback(() => {
    const id = createOptionalEquipmentId()
    dispatch(
      addNewOptionalEquipmentPackage({
        id: configuration?.id,
        equipmentId: id,
      }),
    )
    return id
  }, [dispatch, configuration])

  const updateEquipmentInfo = useCallback(
    (
      id: ConfigurationOptionalEquipmentId,
      title: ConfigurationOptionalEquipment['title'],
      description: ConfigurationOptionalEquipment['description'],
    ) => {
      dispatch(
        updateOptionalEquipmentTitle({
          id: configuration?.id,
          equipmentId: id,
          title,
          description,
        }),
      )
    },
    [dispatch, configuration],
  )

  const updateEquipmentTitle = useCallback(
    (id: ConfigurationOptionalEquipmentId, title: ConfigurationOptionalEquipment['title']) => {
      if (!configuration?.id) {
        return
      }
      const equipment = configuration.optionalEquipment.find((x) => x.id === id)
      if (!equipment) {
        return
      }
      dispatch(
        updateOptionalEquipmentTitle({
          id: configuration?.id,
          equipmentId: id,
          title,
          description: equipment.description,
        }),
      )
    },
    [dispatch, configuration?.id, configuration?.optionalEquipment],
  )

  const addImplicitChanges = useCallback(
    (id: ConfigurationOptionalEquipmentId, entries: ConfigurationOptionalEquipment['implicitChanges']) => {
      if (!configuration?.id) {
        return
      }
      const equipment = configuration.optionalEquipment.find((x) => x.id === id)
      if (!equipment) {
        return
      }
      dispatch(
        replaceEquipmentImplicitVariants({
          id: configuration?.id,
          equipment: id,
          variants: entries,
        }),
      )
    },
    [dispatch, configuration?.id, configuration?.optionalEquipment],
  )

  const addVariantToEquipment = useCallback(
    (equipmentId: ConfigurationOptionalEquipmentId, familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId) => {
      if (!configuration?.id || !enrichment?.enrichedFamilies[familyId]) {
        return
      }
      const family = enrichment.enrichedFamilies[familyId]
      const targetVariant = family?.variants.find((x) => x.id === variantId)
      const sourceVariant = family?.selectedVariant
      const version = configuration.version
      if (!family || !targetVariant) {
        return
      }
      const existingEquipment = configuration.optionalEquipment.find((x) => x.id === equipmentId)
      const existingVariantSelection = existingEquipment?.variants.find((x) => x.family.id === family.id)
      if (existingVariantSelection) {
        toaster.add({
          visibleInMs: 15_000,
          title: 'Overwritten Equipment Variant',
          children: (
            <OverwrittenToastMessage family={family.title} from={existingVariantSelection.targetVariant.title} to={targetVariant.title} />
          ) as ReactNode,
          appearance: 'primary',
        })
      }

      dispatch(
        addEquipmentVariant({
          id: configuration.id,
          equipment: equipmentId,
          family: {
            id: family.id,
            title: family.title,
          },
          targetVariant: {
            id: targetVariant.id,
            title: targetVariant.title,
            price: targetVariant.priceData?.price,
          },
          sourceVariant: sourceVariant
            ? {
                id: sourceVariant.id,
                title: sourceVariant.title,
                price: sourceVariant.priceData?.price,
              }
            : undefined,
          version,
        }),
      )
    },
    [dispatch, configuration, enrichment, toaster.add],
  )

  const removeVariantFromEquipment = useCallback(
    (equipmentId: ConfigurationOptionalEquipmentId, familyId: EnhancedProductFamilyId, variantId: EnhancedProductVariantId) => {
      if (!configuration?.id || !enrichment?.enrichedFamilies[familyId]) {
        return
      }
      const family = enrichment.enrichedFamilies[familyId]
      if (!family) {
        return
      }
      dispatch(
        removeEquipmentVariant({
          id: configuration.id,
          equipment: equipmentId,
          family: familyId,
          targetVariant: variantId,
        }),
      )
    },
    [dispatch, configuration?.id, enrichment],
  )

  const duplicateEquipment = useCallback(
    (equipmentId: ConfigurationOptionalEquipmentId) => {
      if (!configuration?.id) {
        return
      }
      dispatch(
        duplicateEquipmentAction({
          id: configuration.id,
          equipment: equipmentId,
        }),
      )
    },
    [dispatch, configuration?.id],
  )

  const removeEquipment = useCallback(
    (equipmentId: ConfigurationOptionalEquipmentId) => {
      if (!configuration?.id) {
        return
      }
      dispatch(
        removeEquipmentAction({
          id: configuration.id,
          equipment: equipmentId,
        }),
      )
    },
    [dispatch, configuration?.id],
  )

  const setEquipmentStatus = useCallback(
    (equipmentId: ConfigurationOptionalEquipmentId, status: ConfigurationOptionalEquipment['status']) => {
      if (!configuration?.id) {
        return
      }
      dispatch(
        setEquipmentStatusAction({
          id: configuration.id,
          equipment: equipmentId,
          status,
        }),
      )
    },
    [dispatch, configuration?.id],
  )

  return {
    createNewEquipment,
    updateEquipmentInfo,
    updateEquipmentTitle,
    addVariantToEquipment,
    removeVariantFromEquipment,
    addImplicitChanges,
    duplicateEquipment,
    removeEquipment,
    setEquipmentStatus,
  }
}

const useEquipmentViewMode = (t: TFunction<'translation'>) => {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  return {
    mode,
    setMode,
    modes: [
      {
        id: 'edit' as const,
        content: t('configuration.optional-equipment.mode-edit'),
      },
      {
        id: 'preview' as const,
        content: t('configuration.optional-equipment.mode-preview'),
      },
    ],
  }
}

export const useEquipmentDropdown = (equipments: ReturnType<typeof useEquipment>) => {
  const location = useRef<HTMLElement>(null)
  const [open, setOpen] = useState<
    | { open: false; family?: undefined; variant?: undefined }
    | {
        open: true
        family: EnhancedProductFamilyId
        variant: EnhancedProductVariantId
      }
  >({ open: false })

  const showEquipmentViewAt = useCallback((where: HTMLElement, family: EnhancedProductFamilyId, variant: EnhancedProductVariantId) => {
    location.current = where
    setOpen({
      open: true,
      family,
      variant,
    })
  }, [])

  const closeEquipmentView = useCallback(() => {
    setOpen({ open: false })
  }, [])

  const leadingOfferEntry = useMemo(() => {
    return (
      open.open &&
      equipments.equipment.find(
        (x) => x.variants.length > 0 && x.variants.at(0)?.targetVariant.id === open.variant && x.variants.at(0)?.family?.id === open.family,
      )
    )
  }, [equipments.equipment, open])

  return {
    location,
    isOpen: open.open,
    familyId: open.family,
    variantId: open.variant,
    showEquipmentViewAt,
    closeEquipmentView,
    /** The one entry where this is the leading offer entry */
    leadingOfferEntry,
  }
}

const useEquipmentOverlay = (
  equipments: ReturnType<typeof useEquipment>,
  configuration: TruckConfiguration | undefined,
  enrichment: ConfigurationEnrichmentData | undefined,
  actions: ReturnType<typeof useEquipmentActions>,
) => {
  const [selectedEquipment, setSelectedEquipment] = useState<ConfigurationOptionalEquipmentId>()
  const [consequences, setConsequences] = useState<Omit<ComparisonResult, 'diff'>>({
    base: undefined,
    comparison: undefined,
  })
  const diff = useConfigurationDifference(
    consequences.base?.configuration,
    consequences.base?.enrichment,
    consequences.comparison?.configuration,
    consequences.comparison?.enrichment,
  )
  const diffContext = useChangesTable({ ...consequences, diff: diff })
  const openEquipmentOverlay = (equipmentId: ConfigurationOptionalEquipmentId) => {
    setSelectedEquipment(equipmentId)
  }
  const closeEquipmentOverlay = (saveChanges: boolean) => {
    if (saveChanges) {
      saveImplicitChanges()
    }
    setSelectedEquipment(undefined)
    setState('idle')
    setConsequences({ base: undefined, comparison: undefined })
  }
  const [state, setState] = useState<'idle' | 'undoing' | 'checking' | 'checked'>('idle')
  const [undoVariant] = useUndoVariantSelectMutation({
    fixedCacheKey: 'check-offer-overlay-mutation',
  })

  const [checkEquipmentMutation] = useSelectVariantsMutation({
    fixedCacheKey: 'check-offer-overlay-mutation',
  })
  const currentEquipment = useMemo(() => {
    if (!selectedEquipment) {
      return
    }
    const equipment = equipments.byId[selectedEquipment]
    return equipment?.status === 'unchecked' ? equipment : undefined
  }, [equipments, selectedEquipment])

  const checkEquipment = useCallback(
    async (equipment: EnhancedOptionalEquipment) => {
      if (!configuration?.uuid || !enrichment || !currentEquipment) {
        return
      }
      if (equipment.checkVersion === configuration.version) {
        return
      }
      if (state !== 'idle') {
        return
      }
      const variants = equipment.variants.filter((x) => x.properties.isValidTarget === 'valid' && x.properties.isApplicable)
      if (variants.length <= 0) {
        return
      }
      setState('checking')

      try {
        const result = await checkEquipmentMutation({
          uuid: configuration?.uuid,
          multiVariantSelectionRequest: {
            variantsForSelection: variants.map((x) => ({
              familyId: x.sourceFamily.id,
              variantId: x.targetVariant.id,
            })),
          },
        })
        if (result.error) {
          throw result.error
        }
        if (!result.data) {
          throw new Error('No data received')
        }

        const newConfiguration = {
          ...configuration,
          title: `${configuration.title} with optional equipment ${equipment.title?.length > 0 ? equipment.title : 'Unnamed equipment'}`,
          productGroups: result.data?.productGroups,
        }
        const newEnrichment = enrichConfiguration(
          newConfiguration.productGroups,
          newConfiguration.configuredFamilies,
          newConfiguration.initialSelection,
          enrichment.marketData,
          {
            favoriteSets: [],
            activeFavoriteSetId: 'none' as FavoriteSetId,
          },
          'read-only',
          newConfiguration.step.system,
          newConfiguration.apiConfiguration,
          newConfiguration.optionalEquipment,
          newConfiguration.tg3?.tg3ConversionInsightsLookup,
          { currentConfigurationVersion: newConfiguration.version, compatibility: newConfiguration.compatibility },
        )

        // this is from the incoming perspective correct - the base that is being compared towards is the change that has been applied
        setConsequences({
          comparison: { configuration, enrichment },
          base: { configuration: newConfiguration, enrichment: newEnrichment },
        })
        setState('undoing')
        await undoVariant({
          uuid: configuration.uuid,
          configurationStepDataOptions: {
            returnCurrentConfiguration: true,
          },
        })

        setState('checked')
        return
      } catch (e) {
        console.error(`Error while checking variants: ${e}`)
      }
      setState('idle')
    },
    [checkEquipmentMutation, configuration, enrichment, currentEquipment, undoVariant, state],
  )

  const saveImplicitChanges = useCallback(() => {
    if (!enrichment || !consequences.base?.enrichment || !currentEquipment || state !== 'checked') {
      return
    }
    const equipment = currentEquipment
    const changes: ConfigurationOptionalEquipment['implicitChanges'] = []
    for (const familyId of diff.affectedFamilies) {
      if (equipment.variants.some((x) => x.sourceFamily.id === familyId)) {
        continue
      }
      const fromFamily = enrichment.enrichedFamilies[familyId]
      const fromVariant = fromFamily?.selectedVariant
      const toFamily = consequences.base.enrichment.enrichedFamilies[familyId]
      const toVariant = toFamily?.selectedVariant
      if (!fromFamily || !fromVariant || !toFamily || !toVariant) {
        continue
      }
      changes.push({
        family: {
          id: familyId,
          title: fromFamily.title,
        },
        sourceVariant: {
          id: fromVariant.id,
          title: fromVariant.title,
          price: fromVariant.priceData?.price,
        },
        targetVariant: {
          id: toVariant.id,
          title: toVariant.title,
          price: toVariant.priceData?.price,
        },
        version: configuration?.version ?? consequences.base.configuration.version,
      })
    }
    if (changes.length > 0) {
      actions.addImplicitChanges(equipment.id, changes)
    }
    actions.setEquipmentStatus(equipment.id, 'checked')
  }, [actions.addImplicitChanges, actions.setEquipmentStatus, enrichment, consequences, currentEquipment, state, diff, configuration])

  return {
    state,
    diffContext,
    checkEquipment,
    openEquipmentOverlay,
    closeEquipmentOverlay,
    saveImplicitChanges,
    currentEquipment,
  }
}

export const useAddOptionOverlay = (
  configuration: TruckConfiguration | undefined,
  enrichment: ConfigurationEnrichmentData | undefined,
  dispatch: AppDispatch,
) => {
  const [equipment, setEquipment] = useState<EnhancedOptionalEquipment>()
  const openOptionOverlay = (equipment: EnhancedOptionalEquipment) => {
    setEquipment(equipment)
  }
  const closeOptionOverlay = (addSelectedVariants: boolean) => {
    if (addSelectedVariants && equipment && configuration && enrichment) {
      for (const selectedVariantPath of selectedVariant) {
        const variant = enrichment.byPath[selectedVariantPath]
        if (!variant) {
          continue
        }
        const sourceVariant = variant.parentFamily.selectedVariant
        dispatch(
          addEquipmentVariant({
            id: configuration.id,
            equipment: equipment.id,
            family: {
              id: variant.parentFamily.id,
              title: variant.parentFamily.title,
            },
            sourceVariant: sourceVariant
              ? {
                  id: sourceVariant.id,
                  title: sourceVariant.title,
                  price: sourceVariant.priceData?.price,
                }
              : undefined,
            targetVariant: {
              id: variant.id,
              title: variant.title,
              price: variant.priceData?.price,
            },
            version: configuration.version,
          }),
        )
      }
    }
    setEquipment(undefined)
    setSearchWord(undefined)
    setSelectedVariant([])
  }
  const [searchWord, setSearchWord] = useState<string>()
  const [internalSearchWord, setInternalSearchWord] = useState<string>()
  const [showOnlyUnselected, setShowOnlyUnselected] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState<VariantProductPath[]>([])
  const clearSearchWord = useCallback(() => {
    setSearchWord(undefined)
    setInternalSearchWord(undefined)
  }, [])
  const toggleVariantSelection = useCallback((variant: AppProductVariant) => {
    setSelectedVariant((value) =>
      value.includes(variant.path)
        ? value.filter((x) => x !== variant.path)
        : [...value.filter((x) => !x.startsWith(variant.parentFamily.path)), variant.path],
    )
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setInternalSearchWord(searchWord)
    }, 250)
    return () => clearTimeout(timeout)
  }, [searchWord])

  const [variantsByKey, orderedVariants] = useMemo(() => {
    if (!internalSearchWord || !enrichment) {
      return [{}, []]
    }
    const variantsByKey: Record<ProductPath, AppProductVariant> = {}
    const variants: { id: ProductPath; variant: AppProductVariant }[] = []
    const allVariants = getAllVariants(enrichment.rootGroups).filter((x) => x.source === 'ecm')

    for (const variant of allVariants) {
      if (!variant) {
        continue
      }
      // filter out variants that are basically not selectable
      if (showOnlyUnselected && variant.isSelected) {
        continue
      }

      // filter out variants that are pinned to the top
      if (selectedVariant.includes(variant.path)) {
        continue
      }

      if (
        productFamilyContainsString(variant.parentFamily, internalSearchWord) ||
        productVariantContainsString(variant.parentFamily, variant, internalSearchWord)
      ) {
        variants.push({ id: variant.path, variant })
        variantsByKey[variant.path] = variant
      }
    }
    return [variantsByKey, variants]
  }, [internalSearchWord, enrichment, showOnlyUnselected, selectedVariant])

  return {
    isOpen: equipment !== undefined,
    currentEquipment: equipment,
    openOptionOverlay,
    closeOptionOverlay,
    setSearchWord,
    clearSearchWord,
    toggleVariantSelection,
    showOnlyUnselected,
    setShowOnlyUnselected,
    selectedVariant,
    searchWord,
    enrichment,
    foundOptions: {
      byKey: variantsByKey,
      inOrder: orderedVariants,
    },
  }
}

export const useOptionalEquipment = (
  dispatch: AppDispatch,
  configuration: TruckConfiguration | undefined,
  enrichment: ConfigurationEnrichmentData | undefined,
  t: TFunction,
) => {
  const viewMode = useEquipmentViewMode(t)
  const equipment = useEquipment(configuration, enrichment)
  const actions = useEquipmentActions(dispatch, configuration, enrichment)
  const dropdown = useEquipmentDropdown(equipment)
  const overlay = useEquipmentOverlay(equipment, configuration, enrichment, actions)
  const addOption = useAddOptionOverlay(configuration, enrichment, dispatch)
  return {
    viewMode,
    dropdown,
    actions,
    overlay,
    addOption,
    t,
    ...equipment,
  }
}
